import ReactTooltip from 'react-tooltip';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForecast } from '../../../contexts/ForecastContext';
import Icon from '../../Icon/Icon';
import { newCampaignActions, notificationsActions } from '../../../store';
import CommonButton from '../../Buttons/CommonButton';
import Providers from '../WizardAudiences/ProvidersTabs/Providers';
import ForecastMetrics from '../WizardAudiences/ForecastMetrics/ForecastMetrics';

export default function CalculateForecast({
    campaign,
    newCampaign,
    dispatch,
    service,
    setSelectedScreensLocal,
    onUpdateSelectedScreens,
    budgets,
    isActive,
    setCalculatingForecast,
    calculatingForecast,
    selectedScreensLocal = [],
    tierList,
    onClearScreensWithoutProvider,
    generalDemographic = false,
    basics,
    totalSpent = 0,
}) {
    const { t } = useTranslation();
    const navProvidersRef = useRef();
    const {
        forecast,
        forecastTable,
        setForecast,
        setForecastTable,
        setForecastRequestKey,
    } = useForecast();

    const [hasAnyProvider, setHasAnyProvider] = useState(false);
    const [valid, setValidation] = useState(false);
    const [errors, setErrors] = useState([]);
    const normalizeNumericValue = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

        const normalized = String(value).replace(/[^0-9.-]/g, '');
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const forecastDistribution = forecastTable || [];
    const endDate = basics?.timing?.periodValue
        ? new Date(basics.timing.periodValue[1])
        : null;
    const today = new Date();
    const tomorrow = new Date(today);
    const forecastGraph = forecastDistribution.map((data) => ({
        id: data.screenType,
        value: Number(data.value) || 0,
    }));

    const mergedCampaign = useMemo(() => {
        if (!campaign && !basics) return {};

        return {
            ...campaign,
            ...Object.fromEntries(
                Object.entries(basics ?? {}).filter(([, v]) => v !== undefined)
            ),
        };
    }, [campaign, basics]);

    const basicWizard = t('sections.campaigns.new-campaign.wizard-tab.basics');

    const addError = (wizard, title, description, errors) => {
        const newError = { wizard, title, description };
        errors.push(newError);
    };

    const minValue = (min, number) => {
        if (number !== '') {
            return number >= min;
        } else {
            return false;
        }
    };

    const selectedAnyDayOrHour = () =>
        mergedCampaign?.timing?.hoursEnabled?.includes('1') ?? false;

    const checkErrors = () => {
        const errors = [];
        const i18nErrors =
            'sections.campaigns.new-campaign.wizard-review.validation-issues.errors.';
        const i18nBasics = 'sections.campaigns.new-campaign.wizard-basics.';
        const i18nAudiences =
            'sections.campaigns.new-campaign.wizard-audiences.';
        const hasSelectedScreens =
            selectedScreensLocal?.length > 0 ||
            (mergedCampaign?.selectedScreens &&
                mergedCampaign.selectedScreens.length > 0);
        if (!hasSelectedScreens) {
            addError(
                basicWizard,
                t(i18nAudiences + 'forecast.screens'),
                t(i18nErrors + 'screens'),
                errors
            );
        }
        if (!minValue(1, mergedCampaign?.budget?.budget)) {
            addError(
                basicWizard,
                t(i18nBasics + 'budget.title'),
                t(i18nErrors + 'budget'),
                errors
            );
        }
        if (
            normalizeNumericValue(totalSpent) > 0 &&
            normalizeNumericValue(mergedCampaign?.budget?.budget) <
                normalizeNumericValue(totalSpent)
        ) {
            addError(
                basicWizard,
                t(i18nBasics + 'budget.title'),
                t(i18nErrors + 'budget-below-spent'),
                errors
            );
        }
        if (
            !mergedCampaign?.budget?.cpmOptimizer &&
            !minValue(1, mergedCampaign?.budget?.cpm)
        ) {
            addError(
                basicWizard,
                t(i18nBasics + 'budget.title'),
                t(i18nErrors + 'CPM'),
                errors
            );
        }
        if (
            mergedCampaign?.dailyCap?.isActive &&
            !mergedCampaign.dailyCap.isDistribution
        ) {
            if (
                mergedCampaign.dailyCap.isBudget &&
                !minValue(1, mergedCampaign.dailyCap.budget)
            ) {
                addError(
                    basicWizard,
                    t(i18nBasics + 'dailyCap.title'),
                    t(i18nErrors + 'daily-cap-budget'),
                    errors
                );
            }
            if (
                mergedCampaign.dailyCap.isImpressions &&
                !minValue(1, mergedCampaign.dailyCap.impressions)
            ) {
                addError(
                    basicWizard,
                    t(i18nBasics + 'dailyCap.title'),
                    t(i18nErrors + 'daily-cap-impressions'),
                    errors
                );
            }
        }
        if (!mergedCampaign?.timing?.periodValue) {
            addError(
                basicWizard,
                t(i18nBasics + 'timing.title'),
                t(i18nErrors + 'timing-campaign-duration'),
                errors
            );
        }
        if (mergedCampaign?.timing?.selectedTimezone === '') {
            addError(
                basicWizard,
                t(i18nBasics + 'timing.title'),
                t(i18nErrors + 'timing-timezone'),
                errors
            );
        }
        // if day parting is enable and at least one hour has been selected
        if (mergedCampaign?.timing?.isDayParting && !selectedAnyDayOrHour()) {
            addError(
                basicWizard,
                t(i18nBasics + 'timing.title'),
                t(i18nErrors + 'timing-day-parting'),
                errors
            );
        }
        if (
            mergedCampaign?.buyingRules?.isActive &&
            mergedCampaign?.buyingRules?.rules?.length === 0
        ) {
            addError(
                basicWizard,
                t(i18nBasics + 'buyingRules.title'),
                t(i18nErrors + 'buying-rules'),
                errors
            );
        }
        setErrors(errors);
    };

    useEffect(() => {
        checkErrors();
    }, [campaign, basics, budgets, selectedScreensLocal]);

    useEffect(() => {
        setValidation(errors.length === 0);
    }, [errors]);

    const resolveScreensForForecast = () => {
        if (selectedScreensLocal.length) return selectedScreensLocal;

        if (mergedCampaign?.selectedScreens) {
            return mergedCampaign.selectedScreens;
        }

        return [];
    };

    const onClickCalculateForecast = async () => {
        if (endDate <= tomorrow.getTime()) {
            const errorMessage = `${t(
                'sections.dashboard-individual.end-date-out-of-range-description'
            )}`;
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t(
                        'sections.dashboard-individual.period-out-of-range-title'
                    ),
                    message: errorMessage,
                })
            );
            return;
        }
        setCalculatingForecast(true);
        const selectedScreensForForecast = resolveScreensForForecast();
        const campaignForForecast = {
            ...newCampaign,
            ...basics,
            budget: basics?.budget ?? newCampaign?.budget,
            timing: basics?.timing ?? newCampaign?.timing,
            selectedCurrency:
                basics?.selectedCurrency ?? newCampaign?.selectedCurrency,
            selectedScreens: selectedScreensForForecast,
        };
        const result = await service.calculateForecast(campaignForForecast, {
            generalDemographic,
            selectedScreens: selectedScreensForForecast,
        });
        if (result?.metrics) {
            setForecast(result.metrics);
            setForecastTable(result.distributionRows || []);
            if (
                Array.isArray(result.metrics.schedules) &&
                result.metrics.schedules.length > 0
            ) {
                setForecastRequestKey(result.requestKey || '');
            }
        }
        setCalculatingForecast(false);
    };

    useEffect(() => {
        if (isActive && navProvidersRef.current) {
            setHasAnyProvider(navProvidersRef.current.children.length > 0);
        }
    }, [isActive]);

    // for when the campaign have a provider audience and selected other
    // country withoud provider
    if (!isActive && newCampaign.demographics.name) {
        dispatch(newCampaignActions.setDemographics({ id: '' }));
    }

    const hasForecastData =
        !!forecast &&
        ((Array.isArray(forecast.schedules) && forecast.schedules.length > 0) ||
            Number(forecast.costs) > 0 ||
            (typeof forecast.targetImpacts === 'number' &&
                forecast.targetImpacts > 0));
    const shouldRenderContent = hasAnyProvider || hasForecastData;

    return (
        <div className="body rounded-sm p-1">
            <nav className="flex" ref={navProvidersRef}>
                <Providers />
            </nav>
            {shouldRenderContent ? (
                <>
                    <div className="block mt-4">
                        <div className="text-lg font-light">
                            {t(
                                'sections.campaigns.new-campaign.wizard-audiences.forecast.forecast'
                            )}
                        </div>
                        <div className="text-gray-600 flex justify-between">
                            <div>
                                <span className="font-normal">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-audiences.demographics.currency'
                                    )}
                                    : {basics.selectedCurrency.short_name}
                                </span>
                                <br />
                                <span className="text-xs font-bold">
                                    {t(
                                        `sections.campaigns.new-campaign.wizard-audiences.forecast.audiences-info`
                                    )}
                                </span>
                            </div>
                            <div>
                                <CommonButton
                                    disabled={!valid}
                                    className={
                                        newCampaign.demographics.id === '' &&
                                        'invisible'
                                    }
                                    useLoader={true}
                                    type="primaryButton"
                                    onClick={onClickCalculateForecast}
                                    label={t(
                                        'sections.campaigns.new-campaign.wizard-audiences.forecast.calculate-forecast'
                                    )}
                                    styleType={'primary'}
                                />

                                {valid ? (
                                    ''
                                ) : (
                                    <span className="mr-2">
                                        {' '}
                                        <ReactTooltip
                                            effect="solid"
                                            className="w-48 "
                                            id="notValid"
                                            aria-haspopup="true">
                                            <ul className="list-none text-white">
                                                {errors.map((error, index) => {
                                                    return (
                                                        <li
                                                            key={index}
                                                            className="py-1">
                                                            <span className="text-lg text-white">
                                                                {error.title}
                                                                :{' '}
                                                            </span>{' '}
                                                            {error.description}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </ReactTooltip>
                                        <span data-tip data-for="notValid">
                                            <Icon
                                                name="info-circle"
                                                className="text-gray-700 text-sm"
                                            />
                                        </span>{' '}
                                        <br />
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-start content-center my-2 p-2 rounded bg-gray-100 text-gray-500">
                            <div className="ml-2 mr-4 mt-2">
                                <Icon
                                    name="comment-alt-lines"
                                    className="text-gray-400 text-2xl"
                                />
                            </div>
                            <div>
                                {t(
                                    'sections.campaigns.new-campaign.wizard-audiences.forecast.according-your-targeting-selection'
                                )}
                            </div>
                        </div>
                        <ForecastMetrics
                            setSelectedScreensLocal={setSelectedScreensLocal}
                            selectedScreensLocal={selectedScreensLocal}
                            tierList={tierList}
                            campaign={campaign}
                            basics={basics}
                            calculatingForecast={calculatingForecast}
                            onClearScreensWithoutProvider={
                                onClearScreensWithoutProvider
                            }
                            onUpdateSelectedScreens={onUpdateSelectedScreens}
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className="mt-2">
                        <span>
                            {t(
                                'sections.campaigns.new-campaign.wizard-audiences.no-providers-message'
                            )}
                            .
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
